import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IconSearch, IconClose } from "./icons";
import { CatalogController } from "../controllers/catalog";
import { search } from "../lib/searchIndex";
import { useCurrency } from "../context/CurrencyContext";
import styles from "./SearchOverlay.module.css";

// Header search. Loads the catalog lazily on first open, builds the
// in-memory index, and runs queries against it as the user types.
export default function SearchOverlay({ open, onClose }) {
  const [catalog, setCatalog] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef(null);
  const nav = useNavigate();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    CatalogController.list({}).then((res) => {
      if (cancelled) return;
      const list = res?.items ?? res ?? [];
      setCatalog(list);
      setLoaded(true);
    }).catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [open, loaded]);

  useEffect(() => {
    if (open) {
      // Defer focus so the input is mounted.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
      return () => {
        clearTimeout(t);
        window.removeEventListener("keydown", onKey);
        document.body.style.overflow = "";
      };
    }
  }, [open, onClose]);

  const results = useMemo(() => search(catalog, q, { limit: 12 }), [catalog, q]);

  if (!open) return null;

  function go(id) {
    onClose?.();
    setQ("");
    nav(`/product/${encodeURIComponent(id)}`);
  }

  function submit(e) {
    e.preventDefault();
    if (results[0]) go(results[0].id ?? results[0].sku);
  }

  return (
    <div className={styles.scrim} onClick={onClose} role="dialog" aria-modal="true" aria-label="Search">
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <form className={styles.bar} onSubmit={submit}>
          <IconSearch className={styles.icon} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search packs, gear, accessories…"
            className={styles.input}
            inputMode="search"
            autoComplete="off"
            aria-label="Search query"
          />
          <button type="button" onClick={onClose} className={styles.close} aria-label="Close search">
            <IconClose />
          </button>
        </form>
        <div className={styles.body}>
          {!loaded && <div className={styles.muted}>Loading catalog…</div>}
          {loaded && q.trim().length === 0 && (
            <div className={styles.muted}>Try a name (“Assault”), a use (“Hunting”) or a SKU.</div>
          )}
          {loaded && q.trim().length > 0 && results.length === 0 && (
            <div className={styles.muted}>No matches for “{q}”.</div>
          )}
          <ul className={styles.list}>
            {results.map((it) => {
              const id = it.id ?? it.sku;
              return (
                <li key={id}>
                  <button
                    type="button"
                    className={styles.row}
                    onClick={() => go(id)}
                  >
                    <img src={it.image} alt="" className={styles.thumb} loading="lazy" />
                    <div className={styles.meta}>
                      <div className={styles.name}>{it.name}</div>
                      <div className={styles.sub}>
                        <span>{it.productLine}</span>
                        {it.volume ? <span>· {it.volume} L</span> : null}
                      </div>
                    </div>
                    <div className={styles.price}>{formatPrice(it)}</div>
                  </button>
                </li>
              );
            })}
          </ul>
          {results.length > 0 && (
            <Link to="/items" className={styles.viewAll} onClick={onClose}>
              View all items →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
